// User-friendly sections panel. Sections are the building blocks that
// stack vertically to make a page (Header on top, Footer at the bottom,
// Main in the middle). Each section has its own content + appearance.
//
// We deliberately keep the editing surface simple here: add/remove blocks,
// quick-edit common text fields, reorder. Full block editing still happens
// in the main canvas for the Main section.

import { useState } from 'react'
import {
  ChevronRight, ChevronDown, Plus, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react'

import { newSectionId, removeSection, moveSection } from './migrate'
import { makeBlock } from './blocks'
import { nextBlockIdForType } from './pageOps'
import { ColorPickerField } from './ColorPickerField'
import { AssetPickerField } from './AssetPicker'
import type { Block, BlockType, PageModel, Section } from './types'

interface Props {
  page: PageModel
  project: string
  onChange: (next: PageModel) => void
}

const TAG_LABEL: Record<NonNullable<Section['tag']>, string> = {
  header: 'Header',
  main: 'Main content',
  footer: 'Footer',
  aside: 'Aside',
  popup: 'Pop-up',
  custom: 'Custom',
}
const TAG_DESC: Record<NonNullable<Section['tag']>, string> = {
  header: 'Sits at the very top of every viewport — typically a logo, nav, or call-to-action banner.',
  main: 'The body of the page. This is the area you edit in the main canvas.',
  footer: 'Sits at the bottom — copyright, links, contact info.',
  aside: 'A side region — promo strip, filters, or callouts.',
  popup: 'Floating buttons / links that open content (chat, settings…) in a modal dialog.',
  custom: 'A free-form section.',
}

/** Insert a section in the right place based on its tag — Header at top,
 *  Footer at bottom, everything else just before the Footer. */
function smartInsert(model: PageModel, sec: Section): PageModel {
  const sections = [...(model.sections ?? [])]
  let at = sections.length
  if (sec.tag === 'header') {
    at = 0
  } else if (sec.tag === 'footer') {
    at = sections.length
  } else {
    const footerIdx = sections.findIndex(s => s.tag === 'footer')
    at = footerIdx >= 0 ? footerIdx : sections.length
  }
  sections.splice(at, 0, sec)
  // Recompute the editor's flat `blocks` mirror from the (possibly new) main section.
  const main = sections.find(s => (s.tag ?? 'main') === 'main')
  return {
    ...model,
    sections,
    pageSchemaVersion: 2,
    blocks: main?.blocks ?? model.blocks,
  }
}

export function SectionTree({ page, project, onChange }: Props) {
  const sections = page.sections ?? []
  const hasHeader = sections.some(s => s.tag === 'header')
  const hasFooter = sections.some(s => s.tag === 'footer')

  const addSection = (tag: Section['tag']) => {
    const sec: Section = {
      id: newSectionId(),
      name: TAG_LABEL[tag ?? 'main'] ?? 'Section',
      tag,
      // Pre-populate header/footer with a heading so the user sees
      // something immediately in the preview.
      blocks: tag === 'header' || tag === 'footer'
        ? [{ ...makeBlock('heading', nextBlockIdForType(page, 'heading')), level: tag === 'header' ? 1 : 3, text: tag === 'header' ? 'Your headline' : '© Your company' } as Block]
        : [],
    }
    onChange(smartInsert(page, sec))
  }

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{
        background: 'var(--color-bg-secondary)', padding: '10px 12px',
        borderRadius: 6, fontSize: 13, color: 'var(--color-text-secondary)',
        lineHeight: 1.5,
      }}>
        Sections stack from top to bottom. <strong>Main</strong> is the
        body of your page — edit it in the main canvas. Add a
        <strong> Header </strong>or<strong> Footer </strong>to wrap
        content above or below.
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {!hasHeader && <AddBtn onClick={() => addSection('header')} label="+ Add header" />}
        {!hasFooter && <AddBtn onClick={() => addSection('footer')} label="+ Add footer" />}
        <AddBtn onClick={() => addSection('aside')}  label="+ Aside" />
        <AddBtn onClick={() => addSection('custom')} label="+ Section" />
      </div>

      {sections.length === 0 && (
        <div style={emptyStyle}>
          No sections yet — your page is rendered as a single Main section.
        </div>
      )}

      {sections.map((s, idx) => (
        <SectionRow
          key={s.id}
          page={page}
          project={project}
          section={s}
          index={idx}
          total={sections.length}
          onChange={(next) => {
            const arr = sections.map(x => (x.id === s.id ? next : x))
            // If the user edits the Main section's blocks here, mirror them
            // back into page.blocks so the canvas updates.
            const main = arr.find(x => (x.tag ?? 'main') === 'main')
            onChange({
              ...page,
              sections: arr,
              pageSchemaVersion: 2,
              blocks: main?.blocks ?? page.blocks,
            })
          }}
          onRemove={() => onChange(removeSection(page, s.id))}
          onMoveUp={() => onChange(moveSection(page, s.id, idx - 1))}
          onMoveDown={() => onChange(moveSection(page, s.id, idx + 1))}
        />
      ))}
    </div>
  )
}

/* ─── Section row ──────────────────────────────────────────────── */

interface RowProps {
  page: PageModel
  project: string
  section: Section
  index: number
  total: number
  onChange: (next: Section) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}
function SectionRow({ page, project, section, index, total, onChange, onRemove, onMoveUp, onMoveDown }: RowProps) {
  const isMain = (section.tag ?? 'main') === 'main'
  const [open, setOpen] = useState(!isMain)
  const tagKey = (section.tag ?? 'main') as NonNullable<Section['tag']>
  const ringColor =
    tagKey === 'header' ? '#3b82f6' :
    tagKey === 'footer' ? '#6366f1' :
    tagKey === 'main'   ? '#10b981' :
    '#a855f7'

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 8,
      background: 'var(--color-bg-secondary)', overflow: 'hidden',
      borderLeft: `4px solid ${ringColor}`,
    }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 10 }}>
        <button type="button" onClick={() => setOpen(o => !o)} style={iconBtn} title={open ? 'Collapse' : 'Expand'}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>{TAG_LABEL[tagKey]}</strong>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', padding: '1px 6px', background: 'var(--color-bg)', borderRadius: 3 }}>
              {section.blocks.length} block{section.blocks.length === 1 ? '' : 's'}
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            {TAG_DESC[tagKey]}
          </div>
        </div>
        <button type="button" onClick={onMoveUp}   disabled={index === 0}        style={iconBtn} title="Move up"><ArrowUp size={12} /></button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1} style={iconBtn} title="Move down"><ArrowDown size={12} /></button>
        {!isMain && (
          <button type="button" onClick={onRemove} style={{ ...iconBtn, color: '#dc2626' }} title="Remove section"><Trash2 size={12} /></button>
        )}
      </header>

      {open && (
        <div style={{ padding: 12, borderTop: '1px solid var(--color-border)', background: 'var(--color-bg)', display: 'grid', gap: 12 }}>
          {!isMain && (
            <Field label="Section name (your reference only)">
              <input type="text" value={section.name ?? ''} onChange={e => onChange({ ...section, name: e.target.value })} style={inputStyle} />
            </Field>
          )}

          <BlocksEditor section={section} project={project} pageName={page.obj_name} onChange={onChange} />

          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', userSelect: 'none' }}>
              Appearance & advanced
            </summary>
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                Pick a background color scheme by selecting this section on the canvas — the
                right-hand property panel exposes a swatch picker tied to the schemes defined
                in <strong>Advanced → Color schemes</strong>.
              </p>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={section.lazy ?? false}
                  onChange={e => onChange({ ...section, lazy: e.target.checked })}
                />
                Lazy-load (defer rendering until the user scrolls here)
              </label>
            </div>
          </details>
        </div>
      )}
    </div>
  )
}

/* ─── Inline blocks editor for a section ─────────────────────────────
   Lets the user add/remove/reorder/edit text on the section's blocks
   without leaving the panel. Full editing of layout-heavy blocks (hero,
   pricing, etc.) still routes through the main canvas. */

const QUICK_BLOCK_TYPES: Array<{ type: BlockType; label: string }> = [
  { type: 'heading',    label: 'Heading' },
  { type: 'paragraph',  label: 'Paragraph' },
  { type: 'image',      label: 'Image' },
  { type: 'button',     label: 'Button' },
  { type: 'logo_cloud', label: 'Logo cloud' },
  { type: 'hero',       label: 'Hero' },
  { type: 'cta_banner', label: 'CTA banner' },
  { type: 'feature_grid', label: 'Feature grid' },
  { type: 'newsletter', label: 'Newsletter' },
  { type: 'divider',    label: 'Divider' },
  { type: 'spacer',     label: 'Spacer' },
  { type: 'custom_code', label: 'Custom code' },
]

function BlocksEditor({ section, project, pageName, onChange }: { section: Section; project: string; pageName: string; onChange: (next: Section) => void }) {
  const update = (idx: number, patch: Partial<Block>) => {
    const next = section.blocks.map((b, i) => i === idx ? { ...b, ...patch } as Block : b)
    onChange({ ...section, blocks: next })
  }
  const remove = (idx: number) => onChange({ ...section, blocks: section.blocks.filter((_, i) => i !== idx) })
  const add = (type: BlockType) => {
    // BlocksEditor doesn't have full-page context here, so compute the
    // next id from this section alone. Cross-section collisions are
    // unlikely in practice and the canvas's main insert paths use the
    // page-wide helper, but we keep ids in the new format for parity.
    const re = new RegExp(`^${type}_(\\d+)$`)
    let max = 0
    for (const b of section.blocks) {
      const m = b.id.match(re)
      if (m) max = Math.max(max, parseInt(m[1], 10))
    }
    onChange({ ...section, blocks: [...section.blocks, makeBlock(type, `${type}_${max + 1}`, { pageName })] })
  }
  const move = (idx: number, delta: -1 | 1) => {
    const next = [...section.blocks]
    const to = idx + delta
    if (to < 0 || to >= next.length) return
    ;[next[idx], next[to]] = [next[to], next[idx]]
    onChange({ ...section, blocks: next })
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Blocks in this section
        </strong>
        <details style={{ position: 'relative' }}>
          <summary style={{ ...addBtnStyle, listStyle: 'none', cursor: 'pointer' }}>
            <Plus size={12} /> Add block
          </summary>
          <div style={{
            position: 'absolute', top: '100%', right: 0, zIndex: 5, marginTop: 4,
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 6, padding: 4, minWidth: 160, boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          }}>
            {QUICK_BLOCK_TYPES.map(o => (
              <button
                key={o.type}
                type="button"
                onClick={(e) => { add(o.type); (e.currentTarget.closest('details') as HTMLDetailsElement).open = false }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 10px', fontSize: 13, border: 'none',
                  background: 'transparent', cursor: 'pointer', color: 'var(--color-text)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {o.label}
              </button>
            ))}
          </div>
        </details>
      </header>

      {section.blocks.length === 0 ? (
        <div style={emptyStyle}>No blocks yet — click <em>Add block</em> to start.</div>
      ) : (
        section.blocks.map((b, idx) => (
          <BlockRow
            key={b.id}
            block={b}
            project={project}
            index={idx}
            total={section.blocks.length}
            onPatch={(patch) => update(idx, patch)}
            onRemove={() => remove(idx)}
            onMoveUp={() => move(idx, -1)}
            onMoveDown={() => move(idx, 1)}
          />
        ))
      )}
    </div>
  )
}

interface BlockRowProps {
  block: Block
  project: string
  index: number
  total: number
  onPatch: (patch: Partial<Block>) => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}
function BlockRow({ block, project, index, total, onPatch, onRemove, onMoveUp, onMoveDown }: BlockRowProps) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 6,
      padding: 8, background: 'var(--color-bg-secondary)',
      display: 'grid', gap: 6,
    }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
          {block.type}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button type="button" onClick={onMoveUp}   disabled={index === 0}        style={iconBtn}><ArrowUp size={12} /></button>
          <button type="button" onClick={onMoveDown} disabled={index === total - 1} style={iconBtn}><ArrowDown size={12} /></button>
          <button type="button" onClick={onRemove}  style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={12} /></button>
        </div>
      </header>
      <QuickBlockFields block={block} project={project} onPatch={onPatch} />
    </div>
  )
}

function QuickBlockFields({ block, project, onPatch }: { block: Block; project: string; onPatch: (patch: Partial<Block>) => void }) {
  switch (block.type) {
    case 'heading':
      return (
        <>
          <select
            value={(block as any).level ?? 2}
            onChange={e => onPatch({ level: Number(e.target.value) } as any)}
            style={inputStyle}
          >
            <option value={1}>H1</option>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </select>
          <input type="text" value={(block as any).text ?? ''} onChange={e => onPatch({ text: e.target.value } as any)} placeholder="Heading text" style={inputStyle} />
        </>
      )
    case 'paragraph':
    case 'quote':
      return <textarea rows={2} value={(block as any).text ?? ''} onChange={e => onPatch({ text: e.target.value } as any)} placeholder="Text" style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
    case 'image':
      return (
        <>
          <AssetPickerField project={project} value={(block as any).url} accept="image/*" onChange={url => onPatch({ url } as any)} />
          <input type="text" value={(block as any).alt ?? ''} onChange={e => onPatch({ alt: e.target.value } as any)} placeholder="Alt text" style={inputStyle} />
        </>
      )
    case 'button':
      return (
        <>
          <input type="text" value={(block as any).button?.label ?? ''} onChange={e => onPatch({ button: { ...(block as any).button, label: e.target.value } } as any)} placeholder="Button label" style={inputStyle} />
          <input type="text" value={(block as any).button?.url ?? ''} onChange={e => onPatch({ button: { ...(block as any).button, url: e.target.value } } as any)} placeholder="URL" style={inputStyle} />
        </>
      )
    case 'spacer':
      return (
        <input type="number" value={(block as any).height ?? 48} onChange={e => onPatch({ height: Number(e.target.value) } as any)} placeholder="Height (px)" style={inputStyle} />
      )
    default:
      return (
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          Edit detailed properties of this block in the main canvas.
        </span>
      )
  }
}

/* ─── Styles ──────────────────────────────────────────────────── */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
    </div>
  )
}
function AddBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px', fontSize: 13, fontWeight: 500,
        border: '1px solid var(--color-border)', borderRadius: 4,
        background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid var(--color-border-strong)', borderRadius: 4,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--color-text-secondary)', padding: 4,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}
const addBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', fontSize: 13, fontWeight: 500,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
}
const emptyStyle: React.CSSProperties = {
  fontSize: 13, color: 'var(--color-text-secondary)',
  padding: 12, textAlign: 'center',
  background: 'var(--color-bg-secondary)', borderRadius: 6,
  border: '1px dashed var(--color-border)',
}

// Suppress unused-var warning for the imported color picker.
void ColorPickerField
